/**
 * @module  public/js/utils/smsDeeplink.js
 * @summary Helpers for reading and writing SMS deep link bodies.
 *
 * @description
 * Provides small utilities to pull the `body` query parameter from an SMS
 * deep link and to inject/replace it when the seller edits the message in the
 * Focus Mode flow.
 *
 * ## Exports
 * - extractSmsBodyFromLink(link)
 * - applySmsBodyToDeeplink(link, body)
 * - normalizeSmsNumber(rawPhone)
 * - buildTextDeeplink(rawPhone, body, opts)
 * - buildSmsDeepLink(rawPhone, body, opts)
 * - SMS_PROVIDER
 *
 * @version 1.0.0
 * @since   2025-10-02
 */

/**
 * Pull the `body` query parameter from an SMS deep link, decoding it safely.
 *
 * @param {string} [link=''] - sms:, zoomphonesms:, or similar deep link string.
 * @returns {string} Decoded body contents, or an empty string when absent.
 */
export function extractSmsBodyFromLink(link = '') {
  if (!link || typeof link !== 'string') return '';
  const match = /body=([^&]*)/i.exec(link);
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Add or replace the `body` parameter on an SMS deep link.
 *
 * @param {string} [link=''] - Base sms:/zoomphonesms: link to decorate.
 * @param {string} [body=''] - Message text to embed in the link.
 * @returns {string} Updated deep link including the encoded body parameter.
 */
export function applySmsBodyToDeeplink(link = '', body = '') {
  if (!link) return '';
  if (/^zoomphonesms:/i.test(link)) {
    return link.replace(/[?&#].*$/, '');
  }
  const encoded = encodeURIComponent(body || '');
  if (/body=/i.test(link)) {
    return link.replace(/body=[^&]*/i, `body=${encoded}`);
  }
  const separator = link.includes('?') || link.includes('&') ? '&' : '?';
  return `${link}${separator}body=${encoded}`;
}

/**
 * Normalize an SMS phone number to a minimal E.164 representation.
 *
 * @param {string|number} rawPhone - Raw phone input that may include punctuation.
 * @param {string} [defaultCountryCode='+1'] - Country code applied to 10-digit numbers.
 * @returns {string|null} E.164-ish formatted number or null when unusable.
 */
export function normalizeSmsNumber(rawPhone, defaultCountryCode = '+1') {
  if (!rawPhone) return null;
  const digits = String(rawPhone).replace(/\D/g, '');
  if (!digits) return null;

  if (digits.length === 10) {
    return `${defaultCountryCode}${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }

  if (String(rawPhone).trim().startsWith('+')) {
    return `+${digits}`;
  }

  return `+${digits}`;
}

/**
 * Supported deeplink providers for launching SMS/text applications.
 * @type {{ SYSTEM: 'system', ZOOM: 'zoom' }}
 */
export const SMS_PROVIDER = {
  SYSTEM: 'system',
  ZOOM: 'zoom',
};

/**
 * Build a provider-aware text deeplink that targets OS SMS or Zoom Phone.
 *
 * @param {string|number} rawPhone - Number to encode into the deep link.
 * @param {string} [body=''] - Message body to include in the link.
 * @param {object} [opts] - Optional configuration for the deep link.
 * @param {'system'|'zoom'} [opts.provider='system'] - Target provider scheme.
 * @param {string} [opts.ua] - Optional user agent override for platform detection.
 * @returns {string|null} Provider-specific URL or null when a phone cannot be derived.
 */
export function buildTextDeeplink(
  rawPhone,
  body = '',
  { provider = SMS_PROVIDER.SYSTEM, ua } = {}
) {
  const e164 = normalizeSmsNumber(rawPhone);
  if (!e164) return null;

  const encodedBody = encodeURIComponent(body || '');
  const uaParts = [];
  if (ua) {
    uaParts.push(ua);
  }
  if (typeof navigator !== 'undefined' && navigator?.userAgent) {
    uaParts.push(navigator.userAgent);
  }
  if (typeof window !== 'undefined' && window?.navigator?.userAgent) {
    uaParts.push(window.navigator.userAgent);
  }
  const joinedUa = uaParts.join(' ');
  const isIOS = /iPad|iPhone|iPod/.test(joinedUa);
  if (provider === SMS_PROVIDER.ZOOM) {
    return `zoomphonesms://${e164}`;
  }

  const scheme = 'sms';
  const separator = encodedBody ? (isIOS ? '&' : '?') : '';
  const bodyParam = encodedBody ? `${separator}body=${encodedBody}` : '';

  return `${scheme}:${e164}${bodyParam}`;
}

/**
 * Build a platform-aware sms: deep link while avoiding heavy modal imports.
 *
 * @param {string|number} rawPhone - Number to encode into the deep link.
 * @param {string} [body=''] - Message body to include in the link.
 * @param {object} [opts]
 * @param {string} [opts.userAgent] - Override user agent string for platform detection.
 * @returns {string|null} sms: URL or null when a phone cannot be derived.
 */
export function buildSmsDeepLink(rawPhone, body = '', opts = {}) {
  return buildTextDeeplink(rawPhone, body, {
    provider: SMS_PROVIDER.SYSTEM,
    ua: opts.userAgent ?? opts.ua,
  });
}
